// shadcn form controls behind the legacy *Renderer API. Renderers are often
// called as plain functions (Form's schema renderers), so they stay hook-free
// and return these components, which may use hooks.
import React, { useId } from "react";

import { Checkbox } from "../../shadcn/components/ui/checkbox";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
} from "../../shadcn/components/ui/field";
import { Input } from "../../shadcn/components/ui/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "../../shadcn/components/ui/input-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../shadcn/components/ui/select";
import { Switch } from "../../shadcn/components/ui/switch";
import { Textarea } from "../../shadcn/components/ui/textarea";
import { cn } from "../../shadcn/lib/utils";

export const FieldShell = ({ id, label, error, description, children, className }) => (
  <Field
    data-invalid={error ? true : undefined}
    className={cn("w-full gap-1.5", className)}
  >
    {label ? <FieldLabel htmlFor={id}>{label}</FieldLabel> : null}
    {children}
    {description ? (
      <FieldDescription id={`${id}-description`}>{description}</FieldDescription>
    ) : null}
    {error ? <FieldError id={`${id}-error`}>{error}</FieldError> : null}
  </Field>
);

export const describedBy = (id, error, description) =>
  [description ? `${id}-description` : null, error ? `${id}-error` : null]
    .filter(Boolean)
    .join(" ") || undefined;

// Like the MUI TextField: no `value` prop means the field keeps its own state.
const fieldValue = (value) => (value === undefined ? undefined : value ?? "");

export const TextField = ({
  label,
  name,
  onChange,
  disabled,
  value,
  style,
  error = "",
  placeholder,
  type = "text",
  min,
  max,
  maxLength,
  startAdornment,
  endAdornment,
  autoComplete,
  inputProps = {},
  id: idProp,
  ariaLabel,
}) => {
  const generatedId = useId();
  const id = idProp || generatedId;
  const { onChange: inputOnChange, ...restInputProps } = inputProps;
  const shared = {
    id,
    name,
    type,
    value: fieldValue(value),
    disabled,
    placeholder,
    min,
    max,
    maxLength,
    autoComplete: autoComplete || "on",
    "aria-label": ariaLabel,
    "aria-invalid": error ? true : undefined,
    "aria-describedby": describedBy(id, error),
    // Like MUI, `inputProps` go straight to the <input>.
    ...restInputProps,
    onChange: (event) => {
      inputOnChange?.(event);
      onChange?.(event);
    },
  };

  return (
    <FieldShell id={id} label={label} error={error}>
      {/* Decide by whether the prop is passed, not by its value: an adornment
          that appears while typing must not swap (and blur) the input. */}
      {startAdornment !== undefined || endAdornment !== undefined ? (
        <InputGroup style={style}>
          {startAdornment ? (
            <InputGroupAddon>{startAdornment}</InputGroupAddon>
          ) : null}
          <InputGroupInput {...shared} />
          {endAdornment ? (
            <InputGroupAddon align="inline-end">{endAdornment}</InputGroupAddon>
          ) : null}
        </InputGroup>
      ) : (
        <Input {...shared} style={style} />
      )}
    </FieldShell>
  );
};

export const TextArea = ({
  label = "",
  disabled,
  name,
  onChange,
  value,
  style,
  error = "",
  rows = 5,
  maxCharacters,
  placeholder,
  charactersLeftLabel,
}) => {
  const id = useId();
  const handleChange = (event) => {
    if (maxCharacters && event.target.value.length > maxCharacters) return;
    onChange?.(event);
  };

  return (
    <FieldShell id={id} label={label} error={error}>
      <Textarea
        id={id}
        name={name}
        rows={rows}
        value={fieldValue(value)}
        onChange={handleChange}
        disabled={disabled}
        placeholder={placeholder}
        style={style}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, error)}
      />
      {maxCharacters ? (
        <p className="text-xs text-muted-foreground">
          {charactersLeftLabel}: {maxCharacters - (value?.length || 0)}
        </p>
      ) : null}
    </FieldShell>
  );
};

const optionLabel = (options, value) =>
  options.find((option) => option.value === value)?.text ?? "";

export const SelectField = ({
  options = [],
  name,
  value,
  onChange,
  disabled,
  error = "",
  label,
  multiple,
  colorPicker,
  description,
  ariaLabel,
}) => {
  const id = useId();
  const swatch = (color) => (
    <span
      aria-hidden="true"
      className="inline-block size-4 shrink-0 rounded-full border border-border"
      style={{ backgroundColor: color }}
    />
  );

  return (
    <FieldShell id={id} label={label} error={error} description={description}>
      <Select
        name={name}
        multiple={multiple}
        value={multiple ? value || [] : value ?? ""}
        onValueChange={(nextValue) =>
          onChange?.({ target: { name, value: nextValue } })
        }
        disabled={disabled}
      >
        <SelectTrigger
          id={id}
          className="w-full"
          aria-label={ariaLabel}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(id, error, description)}
        >
          <SelectValue>
            {(current) =>
              multiple ? (
                (current || [])
                  .map((item) => optionLabel(options, item))
                  .join(", ")
              ) : (
                <>
                  {colorPicker && current ? swatch(current) : null}
                  {optionLabel(options, current)}
                </>
              )
            }
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {options.map((item) => (
            <SelectItem key={item.value} value={item.value}>
              {colorPicker ? swatch(item.value) : null}
              {item.text}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FieldShell>
  );
};

export const CheckboxField = ({
  label,
  name,
  value = false,
  onChange,
  disabled,
  error = "",
}) => {
  const id = useId();
  return (
    <Field
      orientation="horizontal"
      data-invalid={error ? true : undefined}
      className="w-auto flex-wrap"
    >
      <Checkbox
        id={id}
        name={name}
        checked={Boolean(value)}
        disabled={disabled}
        aria-describedby={describedBy(id, error)}
        onCheckedChange={(checked) =>
          onChange?.({ target: { name, value: checked, checked } })
        }
      />
      <FieldLabel htmlFor={id} className="flex-none font-normal">
        {label}
      </FieldLabel>
      {error ? (
        <FieldError id={`${id}-error`} className="basis-full">
          {error}
        </FieldError>
      ) : null}
    </Field>
  );
};

export const SwitchField = ({
  label,
  value = false,
  onChange,
  name,
  disabled,
  labelPosition = "left",
  intercomTarget,
}) => {
  const id = useId();
  const text = label ? (
    // Long Greek labels wrap instead of pushing the switch out of view.
    <FieldLabel htmlFor={id} className="w-auto min-w-0 flex-initial font-normal">
      {label}
    </FieldLabel>
  ) : null;
  return (
    <div className="flex h-full items-center gap-2">
      {labelPosition === "left" && text}
      <Switch
        id={id}
        name={name}
        // Stable Intercom tooltip anchor; the generated id changes every load.
        data-intercom-target={
          intercomTarget || (name ? `switch-${name}` : undefined)
        }
        checked={Boolean(value)}
        disabled={disabled}
        onCheckedChange={(checked) =>
          onChange?.({ target: { name, value: checked } })
        }
      />
      {labelPosition === "right" && text}
    </div>
  );
};
